import React, { useCallback, useMemo } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import styles from '@/pages/decedent-details/styles.less';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/common/hooks/index';
import { formatDisplayDate } from '@/common/utils/index';

const InfoBlock = ({ title, data = [], labels = [] }) => (
  <div className={styles.infoBlock}>
    <div className={styles.infoBlockHeader}>{title}</div>
    <div className={styles.infoBlockBody}>
      {data.map((value, ind) => (
        <div key={labels[ind]} className={styles.infoBlockBodyRow}>
          <span className={styles.infoBlockBodyLabel}>{labels[ind]}</span>
          <span className={styles.infoBlockBodyValue}>{value}</span>
        </div>
      ))}
    </div>
  </div>
);

const Decedent = ({ profile, openEdit }) => {
  const { t } = useTranslation();
  const GENERAL_LABELS = [
    t('Name'),
    t('Social Insurance Number'),
    t('Prov / Territ Health Number'),
  ];
  const TERRITORY_LABELS = [t('City'), t('Province / State'), t('Country')];
  const BIRTH_LABELS = [t('Date of birth'), ...TERRITORY_LABELS];
  const DEATH_LABELS = [t('Date of death'), ...TERRITORY_LABELS];
  const { isFr } = useLocale();
  const formatDateOnly = useCallback(
    (value) => formatDisplayDate(value, isFr ? 'fr' : 'en'),
    [isFr]
  );
  const formatted = useMemo(() => {
    if (!profile) return null;
    const {
      firstName,
      lastName,
      middleName,
      socialInsuranceNumber,
      localHealthNumber,
      cityOfBirth,
      provinceOfBirth,
      countryOfBirth,
      dateOfBirth,
      cityOfDeath,
      provinceOfDeath,
      countryOfDeath,
      dateOfDeath,
    } = profile;
    return {
      general: [
        `${firstName} ${middleName} ${lastName}`,
        socialInsuranceNumber,
        localHealthNumber,
      ],
      birth: [
        formatDateOnly(dateOfBirth),
        cityOfBirth,
        provinceOfBirth,
        countryOfBirth,
      ],
      death: [
        formatDateOnly(dateOfDeath),
        cityOfDeath,
        provinceOfDeath,
        countryOfDeath,
      ],
    };
  }, [formatDateOnly, profile]);

  return (
    <div className={styles.decedent}>
      <div className={styles.decedentHeader}>
        <h3>{t('Decedent info')}</h3>
        <div className={styles.edit} onClick={openEdit}>
          <EditIcon /> {t('Edit')}
        </div>
      </div>
      {formatted && (
        <div className={styles.decedentBody}>
          <InfoBlock
            title={t('General info')}
            labels={GENERAL_LABELS}
            data={formatted.general}
          />
          <InfoBlock
            title={t('Birth info')}
            labels={BIRTH_LABELS}
            data={formatted.birth}
          />
          <InfoBlock
            title={t('Death info')}
            labels={DEATH_LABELS}
            data={formatted.death}
          />
        </div>
      )}
    </div>
  );
};

export default Decedent;
